import {
  Button,
  Fieldset,
  Group,
  Modal,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { IconMicrophone, IconPlus, IconUpload } from "@tabler/icons-react";
import { useLanguage } from "../../../stores/LanguageStore";
import { useEffect, useMemo, useState } from "react";
import BackButton from "../../../components/View/BackButton";
import FolderSelector from "../../../components/Selectors/FolderSelector";
import { FileUploader } from "react-drag-drop-files";
import { createFile } from "../../../api/FIleAPI";
import { notifications } from "@mantine/notifications";
import SmvEditor from "../models/smv/SmvEditor";

const supportedTypes = [
  "JPG",
  "JPEG",
  "PNG",
  "GIF",
  "PDF",
  "POT",
  "POTX",
  "PPT",
  "PPTX",
  "CSV",
  "XLS",
  "XLSX",
  "WAV",
  "MP3"
  // "HTML",
  // "DOC",
  // "DOCX",
];

const CreateFileModal = ({ onFinish, actualFolder, isAudio, opener, ...props }) => {
  const lang = useLanguage((s) => s.language);
  const [folder, setFolder] = useState(actualFolder);
  const [opened, setOpened] = useState(false);
  const [files, setFiles] = useState([]);
  const handleOpen = () => {
    setOpened(true);
    setFiles([]);
  };
  const handleClose = () => {
    setOpened(false);
  };

  useEffect(() => {
    setFolder(actualFolder);
  }, [actualFolder]);

  const [incorrectLength, setIncorrectLength] = useState(false);
  const [invalidFile, setInvalidFile] = useState(false);
  const [filesKey, setFilesKey] = useState(false);

  const [dragging, setDragging] = useState(false);

  const [name, setName] = useState("");

  const handleGetFile = (fileList) => {
    const value = fileList?.length ? [...fileList] : [fileList];
    setFiles(value);
    setFilesKey(!filesKey);
    setIncorrectLength(false);
    setInvalidFile(false);

    if (value.length === 1) {
      let newName = value[0].name.split(".");
      newName.pop();
      setName(newName.join("."));
    }
  };

  const filePickerStyles = useMemo(() => {
    if (incorrectLength || invalidFile) {
      return {
        border: "2px dashed",
        boxShadow: "#c00 0 0 8px",
        borderColor: "#cc0000",
        borderRadius: 8,
        padding: 8,
        textAlign: "center",
        cursor: "pointer",
      };
    } else
      return {
        border: "1px dashed",
        borderColor: "#00000066",
        borderRadius: 8,
        padding: 2,
        textAlign: "center",
        cursor: "pointer",
      };
  }, [incorrectLength, invalidFile]);

  const [loading, setLoading] = useState(false);
  const handleSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    let fd = new FormData(e.target);
    let data = Object.fromEntries(fd);

    setLoading(true);

    let requests = [];

    files.forEach((file) => {
      requests.push(
        new Promise((resolve, reject) => {
          createFile({
            name: files.length === 1 ? data.name : file?.name,
            file,
            folder_id: folder?.id || null,
          })
            .then((res) => resolve(res))
            .catch((err) => reject(err));
        })
      );
    });

    Promise.allSettled(requests)
      .then((response) => {
        setTimeout(() => {
          notifications.show({
            message: lang.files.row_highlight["created"],
            color: "tertiary.8",
          });
        }, 300);
        handleClose();
        onFinish();
      })
      .catch((err) => {
        if (err.response.data.state_message === "voice_file_incorrect_length") {
          return setIncorrectLength(true);
        } else if (err.response.data.state_message === "invalid_voice_file") {
          return setInvalidFile(true);
        }
        return setInvalidFile(true);
      })
      .finally(() => setLoading(false));
  };

  return (
    <>
      {
        opener ? opener({
          handleOpen
        }) :
          isAudio ?
            <Button
              leftSection={<IconMicrophone size={18} />}
              radius={"md"}
              color="primary"
              ml={"auto"}
              onClick={handleOpen}
              size="md"
              {...props}
            >
              Enregistrer un message vocal
            </Button>
            :
            <Button
              leftSection={<IconPlus size={18} />}
              radius={"md"}
              color="primary"
              ml={"auto"}
              onClick={handleOpen}
              size="md"
              {...props}
            >
              {lang.files.create.button_tooltip}
            </Button>
      }

      <Modal
        opened={opened}
        onClose={handleClose}
        title={lang.files.create.dialog.title}
        size={"lg"}
        centered
      >
        <Stack component={"form"} onSubmit={handleSubmit}>
          <FileUploader
            handleChange={handleGetFile}
            key={filesKey}
            name="file"
            types={supportedTypes}
            hoverTitle={lang.components.files.drop_here}
            onDraggingStateChange={(d) => {
              setDragging(d);
            }}
            required={!files.length}
            multiple={true}
          >
            <Stack
              align="center"
              gap={"xs"}
              mih={300}
              py={32}
              justify="center"
              style={filePickerStyles}
            >
              <IconUpload size={32} />
              <Text style={{ opacity: dragging ? 0.1 : 1 }}>
                {lang.components.files.drag_drop_here}
              </Text>
              {files.length ? (
                <>
                  <Text fw={600} c={"tertiary.8"}>
                    {lang.files.create.dialog.selected}:
                  </Text>
                  {files.map((file) => (
                    <Text key={file.lastModified} fw={600} c={"tertiary.8"}>
                      {file?.name}
                    </Text>
                  ))}
                </>
              ) : (
                <Text fw={600} c={"red"}>
                  {lang.files.create.dialog.no_file}
                </Text>
              )}
            </Stack>
          </FileUploader>
          {
            isAudio ?
              <Text ta="center" size="sm" c={"dimmed"}>
                ( WAV / MP3 )
              </Text>
              :
              <Text ta="center" size="sm" c={"dimmed"}>
                ( JPG / JPEG / PNG / GIF / PDF / POT / POTX / PPT / PPTX / XLS /
                XLSX )
              </Text>
          }

          {files.length < 2 && (
            <TextInput
              name="name"
              label={lang.files.create.dialog.name}
              placeholder={lang.files.create.dialog.name}
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              mt={"sm"}
            />
          )}

          <FolderSelector value={folder} setValue={setFolder} type={"File"} size="md" />

          <Group justify="flex-end" mt={"sm"}>
            <SmvEditor setFile={handleGetFile} inFile defaultOpened={isAudio} />

            <BackButton onClick={handleClose} size="md" />
            <Button type="submit" loading={loading} size="md">
              {lang.files.create.dialog.submit_button}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};

export default CreateFileModal;
